import { NavLink, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AlertCircle, BellRing, LogOut, Monitor, Moon, Palette, PauseCircle, PenSquare, Search, Settings, Sun, WifiOff } from 'lucide-react';
import type { ThemeMode } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { useMe, useSettings, useUpdateSettings } from '../../hooks/useMe';
import { useMailboxes } from '../../hooks/useMailboxes';
import { useBreakpoint, useOnline } from '../../hooks/useBrowser';
import { useRing } from '../../lib/ring';
import { Avatar } from '../ui/Avatar';
import { Menu } from '../ui/Overlay';
import { useClearLocalSession } from '../settings/SecuritySignOut';
import { PALETTE_SHORTCUT, useCommandPalette } from './CommandPalette';
import { NotificationBell } from './NotificationCenter';
import { MailboxSwitcher } from './MailboxSwitcher';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

function ProfileMenu() {
  const me = useMe().data;
  const navigate = useNavigate();
  const update = useUpdateSettings();
  const clear = useClearLocalSession();
  if (!me) return null;
  const theme = me.settings.theme;
  const nextTheme: Record<ThemeMode, ThemeMode> = { light: 'dark', dark: 'system', system: 'light' };
  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  const signOut = async () => {
    try {
      await api('POST /api/auth/logout');
    } catch {
      /* sign out locally regardless */
    }
    await clear('/login');
  };
  return (
    <Menu
      label="Account"
      align="right"
      width={260}
      header={
        <div className="flex items-center gap-3 px-3 pb-2.5 pt-2.5">
          <Avatar name={me.user.displayName} email={me.user.email} size={36} />
          <div className="min-w-0">
            <p className="truncate text-[0.8125rem] font-semibold text-ink">{me.user.displayName}</p>
            <p className="truncate text-xs text-muted">{me.user.email}</p>
          </div>
        </div>
      }
      trigger={(p) => (
        <button type="button" {...p} aria-label="Your account" className="tip tip-bottom-end flex h-10 w-10 items-center justify-center rounded-full transition-transform hover:scale-105" data-tip={me.user.displayName}>
          <Avatar name={me.user.displayName} email={me.user.email} size={34} ring="rgb(var(--surface))" />
        </button>
      )}
      items={[
        'divider',
        { key: 'settings', label: 'Settings', icon: <Settings className="h-4 w-4" />, onSelect: () => navigate('/settings') },
        { key: 'appearance', label: 'Appearance & colours', icon: <Palette className="h-4 w-4" />, onSelect: () => navigate('/settings/appearance') },
        { key: 'theme', label: `Theme: ${theme[0].toUpperCase()}${theme.slice(1)}`, description: 'Click to cycle light · dark · system', icon: <ThemeIcon className="h-4 w-4" />, onSelect: () => update.mutate({ theme: nextTheme[theme] }) },
        'divider',
        { key: 'signout', label: 'Sign out', icon: <LogOut className="h-4 w-4" />, danger: true, onSelect: () => void signOut() },
      ]}
    />
  );
}

/** Small status pills: offline, automation paused, accounts needing reconnection. Only shown when relevant. */
function StatusPills() {
  const online = useOnline();
  const settings = useSettings();
  const needsReauth = (useMailboxes().data?.mailboxes ?? []).filter((m) => m.status === 'needs_reauth' || m.status === 'error');
  const pill = 'flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition-transform hover:scale-[1.03]';
  return (
    <>
      {!online && (
        <span role="status" className={clsx(pill, 'mp-pill-yellow')}>
          <WifiOff className="h-3.5 w-3.5" /> Offline
        </span>
      )}
      {settings?.globalPause && (
        <NavLink to="/settings/responders" className={clsx(pill, 'mp-pill-salmon')} title="All automatic sends are stopped">
          <PauseCircle className="h-3.5 w-3.5" /> Automation paused
        </NavLink>
      )}
      {needsReauth.length > 0 && (
        <NavLink to="/settings/accounts" className={clsx(pill, 'mp-pill-yellow')} title={needsReauth.map((m) => m.email).join(', ')}>
          <AlertCircle className="h-3.5 w-3.5" /> Reconnect {needsReauth.length === 1 ? needsReauth[0].displayName : `${needsReauth.length} accounts`}
        </NavLink>
      )}
    </>
  );
}

export function TopBar() {
  const navigate = useNavigate();
  const palette = useCommandPalette();
  const ring = useRing();
  const bp = useBreakpoint();
  const mobile = bp === 'mobile';
  return (
    <header className="mp-glass z-30 flex h-16 shrink-0 items-center gap-3 border-b border-hairline px-3 sm:px-5">
      <WorkspaceSwitcher compact={mobile} />
      <MailboxSwitcher compact={mobile || bp === 'tablet'} />
      <div className="hidden items-center gap-2 lg:flex">
        <StatusPills />
      </div>
      <div className="flex min-w-0 flex-1 justify-center px-1">
        <button
          type="button"
          onClick={palette.open}
          aria-label="Search mail or jump anywhere"
          className="group flex h-10 w-full max-w-xl items-center gap-3 rounded-full border border-hairline bg-sunken/70 px-4 text-left text-[0.8125rem] text-muted transition-[background-color,box-shadow] hover:bg-surface hover:shadow-card"
        >
          <Search className="h-4 w-4 shrink-0 transition-transform group-hover:scale-110" />
          <span className="min-w-0 flex-1 truncate">{mobile ? 'Search' : 'Search mail, or jump to any page, account or setting'}</span>
          {!mobile && <span className="kbd">{PALETTE_SHORTCUT}</span>}
        </button>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {!mobile && (
          <button
            type="button"
            onClick={() => navigate('/compose')}
            className="hover-glow mr-1 flex h-10 items-center gap-2 rounded-full bg-primary px-4 text-[0.8125rem] font-semibold text-primary-fg shadow-primary transition-colors hover:bg-primary-hover"
          >
            <PenSquare className="h-4 w-4" /> Compose
          </button>
        )}
        {(ring.pendingCount > 0 || ring.ringingNow) && (
          <NavLink
            to="/reminders"
            aria-label={`${ring.pendingCount} reminders waiting`}
            data-tip={ring.ringingNow ? 'Ringing now' : `${ring.pendingCount} reminder${ring.pendingCount === 1 ? '' : 's'} waiting`}
            className={clsx('tip tip-bottom-end flex h-10 items-center gap-1.5 rounded-full bg-danger-soft px-3 text-xs font-bold text-danger-ink', ring.ringingNow && 'ring-pulse')}
          >
            <BellRing className="h-4 w-4" /> {ring.pendingCount}
          </NavLink>
        )}
        <NotificationBell />
        <ProfileMenu />
      </div>
    </header>
  );
}
